import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { InstructionStep, instructionLoaded } from 'ducks/assemblyInstructions';
import Drill from 'ui/icons/drill.svg?react';
import Title from 'ui/Typography/Title';
import Body from 'ui/Typography/Body';
import Table from './Table';
import screenshotSettings from 'config/screenshotSettings';
import { AspectRatioBox, ScreenshotImage } from 'views/Summary/imageComponents';
import * as R from 'ramda';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { isMobile } from 'utils/platform';
import media from 'ui/media';
import { useAppDispatch } from 'ducks/hooks';
import { useServiceSettings } from 'contexts/serviceSettings';

const Step = styled.div`
  margin: 0 0 ${({ theme }) => theme.space.l} 0;
  padding: ${({ theme }) => theme.space.s} 0;
  page-break-inside: avoid;
`;

const SharedTextStyle = css`
  margin: 0 ${({ theme }) => theme.space.xxs};
  text-transform: capitalize;
`;

const HeaderStyled = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin: 0 0 ${({ theme }) => theme.space.s} 0;
  ${media.mobile`
    display: block;
  `}
`;

const HeaderProductName = styled(Body)`
  ${SharedTextStyle};
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 0 !important;
`;

const HeaderDescription = styled(Body)`
  ${SharedTextStyle};
  color: ${({ theme }) => theme.colors.grey[700]};
`;

const InstructionBody = styled.div`
  display: flex;
  page-break-inside: avoid;
  ${media.mobile`
    display: block;
  `}
`;

const ImageWrapper = styled.div`
  width: 44%;
  ${media.mobile`
    width: 100%;
  `}
`;

const ImageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const SvgContainer = styled.svg`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 2;
`;

const SvgText = styled.text`
  font-size: calc(1.6rem / 2);
`;

const Rectangle = styled.rect`
  fill: ${({ theme }) => theme.colors.white};
`;

type Props = {
  instructionStep: InstructionStep;
  tableHeaders: Array<string>;
  image: string;
  points: Array<{ x: number; y: number }>;
  width: number;
  instructionStepTitle: string;
  noAssemblyInstructionsText: string;
};

type HeaderProps = {
  title: string;
  productId: string;
  productName: string;
  description: string;
};

const Header = ({ title, productId, productName, description }: HeaderProps) => (
  <HeaderStyled>
    {!isMobile() && <Drill />}
    <Title type={ThemeFontStyleTypeEnum.headingMedium} pb="xxs" mx="xxs">
      {title}
    </Title>

    <HeaderProductName type={ThemeFontStyleTypeEnum.bodyMedium}>
      {productId}, {productName}
    </HeaderProductName>
    <HeaderDescription type={ThemeFontStyleTypeEnum.bodyMedium}>{description}</HeaderDescription>
  </HeaderStyled>
);

function AssemblyInstructionStep({
  instructionStep,
  instructionStepTitle,
  image,
  points,
  tableHeaders,
  noAssemblyInstructionsText
}: Props) {
  const { instructions, productId, productName, description, entityId } = instructionStep;
  const { theme } = useServiceSettings();
  const fontFamily = `${theme.fontFace.family}, ${theme.fontFace.fallbacks.join(',')}`;
  const noInstructionRef = useRef<HTMLDivElement>(null);
  const sortByNestedPosition = R.sortWith([R.ascend(R.path(['position', 'y']))]);
  const dispatch = useAppDispatch();

  const holes = instructions.map(instruction => {
    return {
      position: instruction.position,
      id: instruction.entityId,
      holeNumber: instruction.holeNumber
    };
  });

  const sortedHoles: Array<{ holeNumber: number }> = sortByNestedPosition(holes);

  const labelWidth = 27;
  const labelHeight = 17;
  const xOffset = 37.5;
  const yOffset = 14;

  const onImageLoad = () => {
    dispatch(instructionLoaded(entityId));
  };

  useEffect(() => {
    if (noInstructionRef.current) {
      setTimeout(() => {
        dispatch(instructionLoaded(entityId));
      }, 10);
    }
  }, [noInstructionRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Step data-testid={`assembly-instruction-step-${entityId}`}>
      <Header
        title={instructionStepTitle}
        productId={productId}
        productName={productName}
        description={description}
      />
      {instructions && instructions.length > 0 ? (
        <InstructionBody>
          <ImageWrapper>
            <ImageContainer>
              <AspectRatioBox>
                <SvgContainer
                  viewBox={`0 0 ${screenshotSettings.assemblySingleFrame.size.x} ${screenshotSettings.assemblySingleFrame.size.y}`}
                >
                  {points && points.length > 0 && (
                    <>
                      {sortedHoles.map((hole, index: number) => {
                        if (!points[index]) {
                          return null;
                        }

                        return (
                          <Rectangle
                            key={index}
                            x={points[index].x - xOffset}
                            y={points[index].y - yOffset}
                            rx="4"
                            width={labelWidth}
                            height={labelHeight}
                          />
                        );
                      })}
                      {sortedHoles.map((hole, index: number) => {
                        if (!points[index]) {
                          return null;
                        }

                        return (
                          <SvgText
                            key={index}
                            x={points[index].x + labelWidth / 2 - xOffset}
                            y={points[index].y + 2 + labelHeight / 2 - yOffset}
                            fontFamily={fontFamily}
                            textAnchor="middle"
                            alignmentBaseline="middle"
                          >
                            {hole.holeNumber}
                          </SvgText>
                        );
                      })}
                    </>
                  )}
                </SvgContainer>
                <ScreenshotImage alt={'noImage'} src={image && image} onLoad={onImageLoad} />
              </AspectRatioBox>
            </ImageContainer>
          </ImageWrapper>
          <Table instructions={instructions} headers={tableHeaders} />
        </InstructionBody>
      ) : (
        <InstructionBody ref={noInstructionRef}>
          <Body ml={isMobile() ? '5px' : '45px'} type={ThemeFontStyleTypeEnum.bodyMedium}>
            {noAssemblyInstructionsText}
          </Body>
        </InstructionBody>
      )}
    </Step>
  );
}

export default AssemblyInstructionStep;
